import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Typography,
  Container,
  MenuItem,
  Box,
  IconButton,
  AccordionSummary,
  Button,
  FormControlLabel,
  TextField,
  Accordion,
  AccordionDetails, FormControl
} from '@mui/material';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import styles from "../Modals.styles";
import DefaultModal from "../../default-modal/DefaultModal";
import { types } from "../../../helpers/eventEmitter";
import ModalHeader from "../../modal-header/ModalHeader";
import ModalFooter from "../../modal-footer/ModalFooter";
import { modalTypes, userTypes } from "../../../../constants/field-constants";
import { connect, useDispatch } from "react-redux";
import {
  adminCreateUserRequest,
  adminGetUserDetailsRequest,
  adminUpdateUserRequest,
} from "../../../../../modules/admin/actions";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import { emailValidator, userFormValidator } from "../../../helpers/validator";
import AlertService from "../../../../services/notification.service";
import { selectAdmin } from "../../../../../modules/admin/selectors";
import BlueSwitch from "../../../styled-components/Blueswitch";
import StyledTextField from "../../../styled-components/StyledTextField";
import { selectLPS } from "../../../../../modules/lps-form/selectors";
import { receiveLPSConfigsRequest } from "../../../../../modules/lps-form/actions";
import { selectUser } from '../../../../../modules/login/selectors';
import type { RootState } from '../../../../../reducers';
import { StyledInputLabel, StyledSelect } from '../../../styled-components/StyledSelect';
import { SelectChangeEvent } from '@mui/material/Select';

export type UserIpWhiteListItem = {
  id: number;
  user_id: number;
  ip: string;
  ip_type: string;
  is_allowed: boolean;
  created_at: string;
  updated_at: string;
}

export type UserState = {
  id: number | null;
  name: string;
  surname: string;
  email: string;
  role_id: number | null;
  default_lps_profile_id: number | null,
  // defaultLPSProfile: "",
  ip_restriction: boolean;
  user_ip_whitelist: UserIpWhiteListItem[],
  ipsForDelete: UserIpWhiteListItem[],
}

/** define initial states for modal **/

const roles = [
  {
    value: 1,
    label: userTypes.ADMIN,
  },
  {
    value: 2,
    label: userTypes.USER,
  },
  {
    value: 3,
    label: userTypes.SUPER_ADMIN,
  }
];

const elementOptionsLpsConfig = {
  id: null,
  name: "n/a",
};
const initialUserState: UserState = {
  id: null,
  name: "",
  surname: "",
  email: "",
  role_id: null,
  default_lps_profile_id: null,
  // defaultLPSProfile: "",
  ip_restriction: false,
  user_ip_whitelist: [],
  ipsForDelete: [],
};

const initialIpDetails = {
  ip: "",
  ip_type: "",
  index: -1,
  id: "",
  is_allowed: false,
  modified: false,
};

const initialEditModeState = {
  isEdit: false,
  index: -1,
};

const initialErrorsState = {
  isFilledName: true,
  isFilledSurname: true,
  isFilledEmail: true,
  isFilledRole: true,
  isFilledIP: true,
  isValidEmail: true,
};

function UserModal(props: any) {
  const dispatch = useDispatch();
  const [userId, setUserId] = useState(null);
  const [user, setUser] = useState<UserState>(initialUserState);
  const [ip, setIP] = useState("");
  const [ipType, setIPType] = useState("");
  const [editMode, setEditMode] = useState(initialEditModeState);
  const [editedIpDetails, setEditedIpDetails] = useState(initialIpDetails);
  const [errors, setErrors] = useState(initialErrorsState);
  const [optionsLpsConfig, setOptionsLpsConfig] = useState([]);

  const { userDetails } = props.user;
  const { lpsConfigs } = props.lpsForm;
  const { userRole } = props.userData;

  const handleOpen = (id: any) => {
    if (id) {
      setUserId(id);
    } else {
      setUserId(null);
    }
    dispatch(receiveLPSConfigsRequest());
  };

  useEffect(() => {
    userId !== null && dispatch(adminGetUserDetailsRequest(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    setUser(userDetails);
  }, [dispatch, userDetails]);

  useEffect(() => {
    if (lpsConfigs !== undefined) {
      const newArray: any = [...lpsConfigs, elementOptionsLpsConfig];
      setOptionsLpsConfig(newArray);
    }
  }, [dispatch, lpsConfigs]);

  useEffect(() => {
    if (user.ip_restriction && user.user_ip_whitelist?.length === 0) {
      AlertService.warning('You have enabled the IP restriction switch. Please enter an IP address or turn off the switch.')
    }
    if ((user.ip_restriction && user.user_ip_whitelist && user.user_ip_whitelist?.length > 0) || !user.ip_restriction) {
      setErrors({...errors, isFilledIP: true});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.ip_restriction, user.user_ip_whitelist ])

  const handleClearForm = () => {
    /** clear all fields and error statuses **/
    setUser(initialUserState);
    setEditedIpDetails(initialIpDetails);
    setEditMode(initialEditModeState);
    setErrors(initialErrorsState);
    setUserId(null);
  };

  const handleChecked = (
    type: any,
    event: React.ChangeEvent<HTMLInputElement>,
    selectedIndex: number
  ) => {
    /** update switches in modal by type **/
    if (type === "ip_restriction")
      setUser({ ...user, ip_restriction: event.target.checked });
    if (type === "isActiveIP") {
      const newState = user.user_ip_whitelist.map((item: any, index) => {
        if (index === selectedIndex) {
          return { ...item, is_allowed: event.target.checked, modified: true };
        } else {
          return item;
        }
      });
      // @ts-ignore
      return setUser({ ...user, user_ip_whitelist: newState });
    }
  };

  const handleChange = (
    type: string,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent,
  ) => {
    /** fields and error statuses changing by type **/

    /** fields change block start **/
    if (type === "ip") setIP(event.target.value);
    if (type === "ipType") setIPType(event.target.value);
    if (type === "default_lps_profile_id" && event.target.value === "") {
      setUser({ ...user, [type]: null });
    }
    setUser({ ...user, [type]: event.target.value });

    /** fields change block end **/

    /** errors change block start **/
    if (type === "name") setErrors({ ...errors, isFilledName: true });
    if (type === "surname") setErrors({ ...errors, isFilledSurname: true });
    if (type === "email") {
      setErrors({ ...errors, isFilledEmail: true });
      const isValidEmail = emailValidator(event.target.value);
      if (isValidEmail){
        setErrors({
          ...errors,
          isValidEmail: isValidEmail,
        });
      }
    }
    if (type === "role_id") setErrors({ ...errors, isFilledRole: true });
    /** errors change block end **/
  };

  const handleSave = (type: string, id: any, user: UserState, closeModal: any) => {
    /** form validation and saving changes **/

    /**validate all fields before user saving**/
    const isValidUserData = userFormValidator(user);
    setErrors(isValidUserData);
    if (
      Object.keys(isValidUserData).every(
        (item) => isValidUserData[item] === true
      )
    ) {
      if (type === modalTypes.SAVE && !id) {
        dispatch(adminCreateUserRequest(user));
        closeModal();
      }
      if (type === modalTypes.SAVE && id) {
        dispatch(adminUpdateUserRequest(id, user));
        closeModal();
      }
      handleClearForm();
    } else {
     AlertService.warning("User form is not valid");
    }
  };

  const handleDeleteItem = (selectedIndex: any) => {
    /** removing item from ip list **/
    const newStateForDelete = [];
    user.user_ip_whitelist.map((item: any, index) => {
      if (index === selectedIndex && item.id) {
        // @ts-ignore
        return newStateForDelete.push(item);
      } else {
        return item;
      }
    });
    const newState = user.user_ip_whitelist.filter(
      (_, index) => index !== selectedIndex
    );
    return setUser({
      ...user,
      user_ip_whitelist: newState,
      ipsForDelete: newStateForDelete,
    });
  };

  const handleChangeItem = (
    fieldName: string,
    selectedIndex: any,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    /** changing ip item details directly in fields **/
    setEditedIpDetails({
      ...editedIpDetails,
      [fieldName]: event.target.value,
      index: selectedIndex,
      modified: true,
    });
  };

  const handleSetEditMode = (type: string, selectedIndex: any) => {
    /** managing open/close state for ip item change, save changes by click in ip list **/
    if (type === "open") {
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
      user.user_ip_whitelist.forEach((item: any, index) => {
        if (index === selectedIndex) {
          setEditedIpDetails({
            ...item,
            ip: item.ip,
            ip_type: item.ip_type,
            id: item.id,
            index: selectedIndex,
          });
        }
      });
    }
    if (type === "close") {
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
    }
    if (type === "edit") {
      const newState = user.user_ip_whitelist.map((item: any, index) => {
        if (
          index === editedIpDetails.index &&
          index === selectedIndex &&
          editedIpDetails.ip !== ""
        ) {
          return {
            ...item,
            ip: editedIpDetails.ip,
            ip_type: editedIpDetails.ip_type,
            modified: editedIpDetails.modified,
          };
        } else {
          return item;
        }
      });
      // @ts-ignore
      setUser({ ...user, user_ip_whitelist: newState });
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
      setEditedIpDetails(editedIpDetails);
    }
  };

  const addNewIP = () => {
    /** adding new IP to ip list **/
    if (ip) {
      setUser({
        ...user,
        // @ts-ignore
        user_ip_whitelist: [
          // @ts-ignore
          ...user.user_ip_whitelist,
          // @ts-ignore
          { ip, ip_type: ipType, is_allowed: false },
        ],
      });
      setIP("");
      setIPType("");
    }
  };

  const generateKey = (item: {
    ip?: string | null;
    ip_type?: string | null;
  }, index: number) => {
    const ip = item.ip && item.ip.trim() !== "" ? item.ip.trim() : null;
    const ip_type = item.ip_type && item.ip_type.trim() !== "" ? item.ip_type.trim() : null;
    if (ip) {
      return `ip-${ip}-${index}`;
    } else
      if (ipType) {
        return `no-ip-${ip_type}-${index}`;
      } else {
        return `no-ip-no-type-${index}`;
      }
  }
  return (
    <>
      <DefaultModal
        nameEvent={types.openCreateUpdateUserModal}
        onClose={handleClearForm}
        onOpen={handleOpen}
      >
        {({ closeModal }) => (
          <>
            <ModalHeader title={userId ? "EDIT USER" : "CREATE USER"} />
            <Container sx={styles.formHolderUserModal}>
              <Box
                component="form"
                autoComplete="off"
                sx={{ marginBottom: "12px" }}
              >
                <StyledTextField
                  fullWidth
                  required
                  label="Name"
                  margin="dense"
                  onChange={(event) => handleChange("name", event)}
                  value={user.name || ""}
                  error={!errors.isFilledName}
                />
                <StyledTextField
                  fullWidth
                  required
                  label="Surname"
                  margin="dense"
                  onChange={(event) => handleChange("surname", event)}
                  value={(user.surname === undefined ||
                    user.surname === null ||
                    user.surname.length === 0) ? '' : user.surname}
                  error={!errors.isFilledSurname}
                />
                <StyledTextField
                  fullWidth
                  required
                  label="Email"
                  margin="dense"
                  onChange={(event) => handleChange("email", event)}
                  value={user.email || ""}
                  error={!errors.isFilledEmail || !errors.isValidEmail}
                />

                <FormControl
                  fullWidth
                  required
                  error={!errors.isFilledRole}
                  margin="dense"
                >
                  <StyledInputLabel
                    id="select-role-label"
                    htmlFor="select-role-input"
                  >
                    Role
                  </StyledInputLabel>
                  <StyledSelect
                    labelId="select-role-label"
                    id="select-role"
                    name="selectRole"
                    value={user.role_id || ""}
                    label="Role"
                    onChange={(event: SelectChangeEvent) =>  handleChange("role_id", event)}
                    inputProps={{
                      id: 'select-role-input',
                    }}
                  >
                    {roles.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        disabled={(userRole !== userTypes.SUPER_ADMIN && option.label === userTypes.SUPER_ADMIN)
                          || (Number(user.role_id) === 3 && userRole === userTypes.ADMIN ) }
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>

                {/*<StyledTextField*/}
                {/*  fullWidth*/}
                {/*  select*/}
                {/*  required*/}
                {/*  label="Role"*/}
                {/*  margin="dense"*/}
                {/*  defaultValue=""*/}
                {/*  value={user.role_id || ""}*/}
                {/*  error={!errors.isFilledRole}*/}
                {/*  onChange={(event) => handleChange("role_id", event)}*/}
                {/*>*/}
                {/*  {roles.map((option) => (*/}
                {/*    <MenuItem*/}
                {/*      key={option.value}*/}
                {/*      value={option.value}*/}
                {/*      disabled={(userRole !== userTypes.SUPER_ADMIN && option.label === userTypes.SUPER_ADMIN)*/}
                {/*                || (Number(user.role_id) === 3 && userRole === userTypes.ADMIN ) }*/}
                {/*    >*/}
                {/*      {option.label}*/}
                {/*    </MenuItem>*/}
                {/*  ))}*/}
                {/*</StyledTextField>*/}

                <FormControl
                  fullWidth
                  margin="dense"
                >
                  <StyledInputLabel
                    id="select-default-lps-profile-label"
                    htmlFor="select-default-lps-profile-input"
                  >
                    Default LPS profile
                  </StyledInputLabel>
                  <StyledSelect
                    labelId="select-default-lps-profile-label"
                    id="select-default-lps-profile"
                    name="selectDefaultLPSProfile"
                    value={(user.default_lps_profile_id === undefined ||
                      user.default_lps_profile_id === null) ? '' : user.default_lps_profile_id}
                    label="Default LPS profile"
                    onChange={(event: SelectChangeEvent) =>  handleChange("default_lps_profile_id", event)}
                    inputProps={{
                      id: 'select-default-lps-profile-input',
                    }}
                  >
                    {optionsLpsConfig.map(({ id, name }) => (
                      <MenuItem key={id} value={id}>
                        {name}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>

                {/*<StyledTextField*/}
                {/*  fullWidth*/}
                {/*  select*/}
                {/*  label="Default LPS profile"*/}
                {/*  margin="dense"*/}
                {/*  defaultValue=""*/}
                {/*  value={(user.default_lps_profile_id === undefined ||*/}
                {/*    user.default_lps_profile_id === null) ? '' : user.default_lps_profile_id}*/}
                {/*  onChange={(event) => handleChange("default_lps_profile_id", event)}*/}
                {/*>*/}
                {/*  {optionsLpsConfig.map(({ id, name }) => (*/}
                {/*    <MenuItem key={id} value={id}>*/}
                {/*      {name}*/}
                {/*    </MenuItem>*/}
                {/*  ))}*/}
                {/*</StyledTextField>*/}
                <FormControlLabel
                  value="end"
                  control={<BlueSwitch />}
                  label="IP restriction"
                  labelPlacement="end"
                  //@ts-ignore
                  onChange={(event) => handleChecked("ip_restriction", event, -1)}
                  checked={user.ip_restriction}
                  defaultChecked={initialUserState.ip_restriction}
                  style={{ color: "#495057" }}
                />
                <Accordion disabled={!user.ip_restriction}>
                  <AccordionSummary>
                    <Typography component="legend" sx={{color: !errors.isFilledIP ? "error.main" : "#495057"}}>
                      Whitelisted IP’s
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <StyledTextField
                      disabled={!user.ip_restriction}
                      fullWidth
                      required
                      label="IP"
                      margin="dense"
                      onChange={(event) => handleChange("ip", event)}
                      value={ip}
                    />
                    <StyledTextField
                      disabled={!user.ip_restriction}
                      fullWidth
                      label="Type"
                      margin="dense"
                      onChange={(event) => handleChange("ipType", event)}
                      value={ipType}
                    />
                  </AccordionDetails>
                  <Button
                    sx={styles.buttonIp}
                    onClick={addNewIP}
                    disabled={!user.ip_restriction}
                  >
                    Add new IP
                  </Button>
                  {user?.user_ip_whitelist?.length > 0 &&
                    user.user_ip_whitelist.map(
                      (
                        item,
                        index
                      ) => {
                        const key = generateKey (item, index);
                        return editMode.isEdit && editMode.index === index ? (
                          <Box sx={styles.ipHolder} key={key}>
                            <Box sx={styles.editIpHolderFields}>
                              <TextField
                                sx={{ marginRight: "15px", width: "100%" }}
                                label="IP"
                                required
                                variant="standard"
                                onChange={(event) =>
                                  handleChangeItem("ip", index, event)
                                }
                                value={editedIpDetails.ip}
                                disabled={!user.ip_restriction}
                              />
                              <TextField
                                sx={{ width: "100%" }}
                                label="Type"
                                variant="standard"
                                onChange={(event) =>
                                  handleChangeItem("ip_type", index, event)
                                }
                                value={editedIpDetails.ip_type}
                                disabled={!user.ip_restriction}
                              />
                            </Box>
                            <Box sx={styles.editIpHolderButtons}>
                              <IconButton
                                disabled={!user.ip_restriction}
                                onClick={() => handleSetEditMode("edit", index)}
                              >
                                <DoneIcon fontSize={"small"} />
                              </IconButton>
                              <IconButton
                                disabled={!user.ip_restriction}
                                onClick={() =>
                                  handleSetEditMode("close", index)
                                }
                              >
                                <CloseIcon fontSize={"small"} />
                              </IconButton>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={styles.ipHolder} key={index}>
                            <Box sx={styles.ipHolder}>
                              <Typography sx={styles.ipTitle} variant={"h6"}>
                                {item?.ip}
                              </Typography>
                              <Typography sx={styles.ipTitle} variant={"h6"}>
                                {item?.ip_type}
                              </Typography>
                            </Box>
                            <Box sx={styles.ipHolder}>
                              <FormControlLabel
                                control={
                                  <BlueSwitch
                                    sx={{ transform: "scale(0.8)" }}
                                  />
                                }
                                label="Active"
                                disabled={!user.ip_restriction}
                                sx={styles.switchLabel}
                                checked={item?.is_allowed}
                                onChange={(event) =>
                                  //@ts-ignore
                                  handleChecked("isActiveIP", event, index)
                                }
                              />
                              <IconButton
                                aria-label="Edit"
                                disabled={!user.ip_restriction}
                                sx={styles.iconButton}
                                onClick={() => handleSetEditMode("open", index)}
                              >
                                <EditIcon sx={styles.iconStyles} />
                              </IconButton>
                              <IconButton
                                aria-label="Delete"
                                disabled={!user.ip_restriction}
                                sx={styles.iconButton}
                                onClick={() => handleDeleteItem(index)}
                              >
                                <DeleteIcon sx={styles.iconStyles} />
                              </IconButton>
                            </Box>
                          </Box>
                        );
                      }
                    )}
                </Accordion>
              </Box>
            </Container>
            <ModalFooter
              closeModal={closeModal}
              type={modalTypes.CREATE}
              onSave={() =>
                handleSave(modalTypes.SAVE, userId, user, closeModal)
              }
            />
          </>
        )}
      </DefaultModal>
    </>
  );
}

function mapStateToProps(state: RootState) {
  const user = selectAdmin(state);
  const lpsForm = selectLPS(state);
  const userData = selectUser(state);
  return { user, lpsForm, userData};
}

export default connect(mapStateToProps)(UserModal);
